import React, { useState, useEffect } from "react";
import { Layout, Table, Button, Card, message, Space, Modal, Descriptions, Tag, Spin, Input, Form, Dropdown, Row, Col } from "antd";
import { CheckSquareOutlined, LockOutlined, UserOutlined, EyeOutlined, MoreOutlined, SearchOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;

const IssueItems = () => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [detailVisible, setDetailVisible] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    
    // Credentials modal state
    const [verifyVisible, setVerifyVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [search, setSearch] = useState("");

    useEffect(() => {
        fetchReadyNotes();
    }, []);

    const fetchReadyNotes = async () => {
        try {
            setLoading(true);
            const response = await api.get("/inventory/issuing/list.php?iin_status=Ready");
            if (response.data.status === "success") {
                setNotes(response.data.dataset || []);
            } else {
                message.error("Failed to fetch ready issue notes.");
            }
        } catch (error) {
            message.error("Error connecting to issuing API.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDetail = async (record) => {
        try {
            setLoadingDetail(true);
            setDetailVisible(true);
            const response = await api.get(`/inventory/issuing/list.php?iin_id=${record.iin_id}`);
            if (response.data.status === "success") {
                setSelectedNote(response.data.dataset);
            } else {
                message.error("Failed to load details.");
                setDetailVisible(false);
            }
        } catch (error) {
            message.error("Error loading details.");
            setDetailVisible(false);
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleOpenVerify = (record) => {
        setSelectedNote(record);
        setVerifyVisible(true);
        form.resetFields();
    };

    const handleVerifySubmit = async (values) => {
        if (!selectedNote) return;
        try {
            setSubmitting(true);
            const payload = {
                iin_id: Number(selectedNote.iin_id),
                username: values.username,
                password: values.password
            };

            const response = await api.post("/inventory/issuing/collect.php", payload);
            if (response.data.status === "success") {
                message.success(response.data.message || "Items successfully issued!");
                setVerifyVisible(false);
                setDetailVisible(false);
                setSelectedNote(null);
                fetchReadyNotes();
            } else {
                message.error(response.data.message || "Verification failed.");
            }
        } catch (error) {
            message.error("Error during credentials verification.");
        } finally {
            setSubmitting(false);
        }
    };

    // Client-side search over the loaded notes
    const filteredNotes = search.trim()
        ? notes.filter((n) => {
            const q = search.trim().toLowerCase();
            return (
                (n.iin_ref || "").toLowerCase().includes(q) ||
                (n.mr_ref || "").toLowerCase().includes(q) ||
                (n.prepare_user_dname || "").toLowerCase().includes(q)
            );
        })
        : notes;

    const columns = [
        {
            title: "Reference",
            dataIndex: "iin_ref",
            key: "iin_ref",
            render: (text) => <strong style={{ color: 'var(--fluid-primary)' }}>{text}</strong>
        },
        {
            title: "MR Ref",
            dataIndex: "mr_ref",
            key: "mr_ref",
        },
        {
            title: "Prepared By",
            dataIndex: "prepare_user_dname",
            key: "prepare_user_dname",
            render: (text) => text || "-"
        },
        {
            title: "Prepared Date",
            dataIndex: "prepare_dt",
            key: "prepare_dt",
            render: (date) => date ? dayjs(date).format("YYYY-MM-DD hh:mm A") : "-"
        },
        {
            title: "Approved By",
            dataIndex: "complete_user_dname",
            key: "complete_user_dname",
            render: (text) => text || "-"
        },
        {
            title: "Approved Date",
            dataIndex: "completed_dt",
            key: "completed_dt",
            render: (date) => date ? dayjs(date).format("YYYY-MM-DD hh:mm A") : "-"
        },
        {
            title: "Status",
            dataIndex: "iin_status",
            key: "iin_status",
            render: (status) => (
                <Tag color={getStatusColor(status)} style={{ fontWeight: 600 }}>
                    {status}
                </Tag>
            )
        },
        {
            title: "Total Items",
            dataIndex: "total_items",
            key: "total_items",
            align: 'center'
        },
        {
            title: "Total Qty",
            dataIndex: "total_qty",
            key: "total_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Action",
            key: "action",
            align: 'center',
            width: 80,
            render: (_, record) => {
                const items = [
                    {
                        key: "view",
                        label: "View Items",
                        icon: <EyeOutlined />,
                        onClick: () => handleOpenDetail(record)
                    },
                    {
                        key: "issue",
                        label: "Issue / Collect",
                        icon: <CheckSquareOutlined />,
                        onClick: () => handleOpenVerify(record)
                    }
                ];
                return (
                    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                        <Button type="text" icon={<MoreOutlined />} className="fluid-dots-btn" />
                    </Dropdown>
                );
            }
        }
    ];

    const detailColumns = [
        {
            title: "Item Code",
            dataIndex: "in_code",
            key: "in_code"
        },
        {
            title: "Item Name",
            dataIndex: "in_name",
            key: "in_name"
        },
        {
            title: "UOM",
            dataIndex: "uom_name",
            key: "uom_name",
            render: (text) => text || "-"
        },
        {
            title: "Issue Qty",
            dataIndex: "issuenote_qty",
            key: "issuenote_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Serial Numbers",
            dataIndex: "serial_numbers",
            key: "serial_numbers",
            render: (text) => text || "-"
        },
        {
            title: "Special Details",
            dataIndex: "special_details",
            key: "special_details",
            render: (text) => text || "-"
        }
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <AppBreadcrumb items={[
                    { label: 'Inventory', link: '/inventory-items' },
                    { label: 'Issue Items' },
                ]} />
                <h1 className="fluid-page-title">
                    <CheckSquareOutlined style={{ marginRight: 12 }} />Issue Items
                </h1>
                
            </div>

            <Content className="fluid-content">
                <Card className="fluid-card">
                    <div style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    placeholder="Search ref, MR ref, prepared by"
                                    prefix={<SearchOutlined />}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    allowClear
                                />
                            </Col>
                        </Row>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={filteredNotes}
                        rowKey="iin_id"
                        loading={loading}
                        bordered
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            defaultPageSize: 10,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                        }}
                    />
                </Card>
            </Content>

            {/* View Detail Modal */}
            <Modal
                title={`Ready Issue Note Details: ${selectedNote ? selectedNote.iin_ref : ""}`}
                open={detailVisible}
                onCancel={() => {
                    setDetailVisible(false);
                    setSelectedNote(null);
                }}
                footer={[
                    <Button 
                        key="close" 
                        onClick={() => {
                            setDetailVisible(false);
                            setSelectedNote(null);
                        }}
                    >
                        Close
                    </Button>,
                    <Button 
                        key="issue" 
                        type="primary" 
                        icon={<CheckSquareOutlined />} 
                        onClick={() => {
                            setVerifyVisible(true);
                            form.resetFields();
                        }}
                        style={{ background: 'var(--fluid-primary)', borderColor: 'var(--fluid-primary)' }}
                    >
                        Issue / Collect
                    </Button>
                ]}
                width={900}
                destroyOnClose
            >
                {loadingDetail || !selectedNote ? (
                    <div style={{ textAlign: 'center', padding: '30px 0' }}>
                        <Spin size="large" tip="Loading details..." />
                    </div>
                ) : (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Reference"><strong>{selectedNote.iin_ref}</strong></Descriptions.Item>
                            <Descriptions.Item label="Status"><Tag color={getStatusColor(selectedNote.iin_status)}>{selectedNote.iin_status}</Tag></Descriptions.Item>
                            <Descriptions.Item label="MR Ref">{selectedNote.mr_ref}</Descriptions.Item>
                            <Descriptions.Item label="Prepared By">{selectedNote.prepare_user_dname || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Prepared Date">
                                {selectedNote.prepare_dt ? dayjs(selectedNote.prepare_dt).format("YYYY-MM-DD hh:mm A") : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Approved By">{selectedNote.complete_user_dname || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Approved Date">
                                {selectedNote.completed_dt ? dayjs(selectedNote.completed_dt).format("YYYY-MM-DD hh:mm A") : "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Table
                            dataSource={selectedNote.items}
                            columns={detailColumns}
                            rowKey="iinitem_id"
                            pagination={false}
                            bordered
                            size="middle"
                        />
                    </Space>
                )}
            </Modal>

            {/* Collector Verification Modal */}
            <Modal
                title="Verify Collector Credentials"
                open={verifyVisible}
                onCancel={() => setVerifyVisible(false)}
                footer={null}
                width={400}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleVerifySubmit}
                >
                    <Form.Item
                        label="Collector Username"
                        name="username"
                        rules={[{ required: true, message: "Please input the collector username!" }]}
                    >
                        <Input prefix={<UserOutlined />} placeholder="Username" />
                    </Form.Item>
                    <Form.Item
                        label="Collector Password"
                        name="password"
                        rules={[{ required: true, message: "Please input the collector password!" }]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Password" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
                        <Space>
                            <Button danger onClick={() => setVerifyVisible(false)} disabled={submitting}>
                                Cancel
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={submitting}
                                className="fluid-btn-primary"
                            >
                                Verify & Collect
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Footerdiv />
        </Layout>
    );
};

export default IssueItems;
